type BackendErrors = Record<string, string | string[] | undefined>;

export type LocalizedClientApiError = {
  status: number | null;
  message: string;
  errors: Record<string, string>;
};

const FIELD_LABELS: Record<string, string> = {
  fullname: "ФИО",
  phone: "Телефон",
  telegram: "Telegram",
  whatsapp: "WhatsApp",
  instagram: "Instagram",
  query: "Запрос сделки",
  propertyPrice: "Итоговая сумма сделки",
  finalCalculationId: "Итоговый расчёт",
  dealStatusId: "Статус сделки",
  periodFrom: "Дата начала периода",
  periodTo: "Дата окончания периода",
};

const STATUS_MESSAGES: Record<number, string> = {
  400: "Сервер не смог обработать запрос. Проверьте введённые данные.",
  401: "Сессия завершилась. Войдите в аккаунт ещё раз.",
  403: "У вас нет доступа к этому действию.",
  404: "Клиент не найден. Возможно, он уже был удалён.",
  409: "Данные клиента конфликтуют с уже существующей записью.",
  419: "Сессия завершилась. Войдите в аккаунт ещё раз.",
  422: "Проверьте правильность заполнения полей.",
  429: "Слишком много запросов. Попробуйте ещё раз чуть позже.",
  500: "На сервере произошла ошибка. Попробуйте ещё раз позже.",
  502: "Сервис временно недоступен. Попробуйте ещё раз позже.",
  503: "Сервис временно недоступен. Попробуйте ещё раз позже.",
  504: "Сервер не успел ответить. Попробуйте ещё раз.",
};

const hasRussianText = (value: string) => /[А-Яа-яЁё]/.test(value);

const getFieldLabel = (field: string) => FIELD_LABELS[field] ?? "Поле";

const translateValidationMessage = (message: string, field: string) => {
  const normalized = message.trim();

  if (!normalized) {
    return `${getFieldLabel(field)}: проверьте значение.`;
  }

  if (hasRussianText(normalized)) {
    return normalized;
  }

  const label = getFieldLabel(field);
  const maxMatch = normalized.match(
    /(?:may not be greater than|must not be greater than|must be less than or equal to) (\d+)/i,
  );
  const minMatch = normalized.match(
    /(?:must be at least|must have at least) (\d+)/i,
  );

  if (/required/i.test(normalized)) {
    return `${label} — обязательное поле.`;
  }

  if (/after or equal|after_or_equal/i.test(normalized)) {
    return "Дата окончания периода не может быть раньше даты начала.";
  }

  if (/already been taken|already exists|unique/i.test(normalized)) {
    return `${label}: такое значение уже используется.`;
  }

  if (maxMatch) {
    return `${label}: не более ${maxMatch[1]} символов.`;
  }

  if (minMatch) {
    return `${label}: не менее ${minMatch[1]} символов.`;
  }

  if (/valid|invalid|format|regex/i.test(normalized)) {
    return `${label}: неверный формат.`;
  }

  if (/string/i.test(normalized)) {
    return `${label}: укажите текстовое значение.`;
  }

  if (/integer|number|numeric/i.test(normalized)) {
    return `${label}: укажите корректное числовое значение.`;
  }

  return `${label}: проверьте значение.`;
};

const localizeFieldErrors = (errors: BackendErrors | undefined) => {
  if (!errors || typeof errors !== "object") {
    return {};
  }

  return Object.entries(errors).reduce<Record<string, string>>(
    (result, [field, messages]) => {
      const firstMessage = Array.isArray(messages) ? messages[0] : messages;

      if (typeof firstMessage === "string") {
        result[field] = translateValidationMessage(firstMessage, field);
      }

      return result;
    },
    {},
  );
};

/**
 * Не выводим неизвестный англоязычный текст от API напрямую: пользователь
 * всегда получает русское сообщение, а техническая причина остаётся в консоли.
 */
export const localizeClientApiError = (
  error: any,
  fallbackMessage: string,
): LocalizedClientApiError => {
  const status = error?.response?.status ?? null;
  const responseData = error?.response?.data;
  const backendMessage =
    typeof responseData?.message === "string"
      ? responseData.message.trim()
      : "";
  const errors = localizeFieldErrors(responseData?.errors);
  const hasFieldErrors = Object.keys(errors).length > 0;

  let message = fallbackMessage;

  if (backendMessage && hasRussianText(backendMessage)) {
    message = backendMessage;
  } else if (hasFieldErrors) {
    message = STATUS_MESSAGES[422];
  } else if (status && STATUS_MESSAGES[status]) {
    message = STATUS_MESSAGES[status];
  }

  return {
    status,
    message,
    errors,
  };
};
